import { Button } from '@creanizer/ui'
import { Copy, Download, Save, FileText, FileCode, Globe } from 'lucide-react'
import type { WikipediaResult } from '@creanizer/types'

interface WikipediaDisplayProps {
  result: WikipediaResult
  onSave: () => void
  isSaved?: boolean
}

export function WikipediaDisplay({ result, onSave, isSaved }: WikipediaDisplayProps) {
  // Null/undefined check
  if (!result || !result.title) {
    return (
      <div className="card">
        <div className="p-6">
          <p className="text-muted-foreground">結果が生成されていません</p>
        </div>
      </div>
    )
  }
  const handleCopy = () => {
    const text = formatAsMarkdown(result)
    navigator.clipboard.writeText(text)
    alert('クリップボードにコピーしました')
  }

  const handleExportJson = () => {
    const data = JSON.stringify(result, null, 2)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${result.title}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExportMarkdown = () => {
    const markdown = formatAsMarkdown(result)
    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${result.title}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExportHtml = () => {
    const html = formatAsHtml(result)
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${result.title}.html`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card">
      <div className="p-6">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold">{result.title}</h3>
          <div className="flex gap-2">
            <Button 
              onClick={onSave} 
              size="sm"
              variant={isSaved ? 'secondary' : 'default'}
              disabled={isSaved}
            >
              <Save className="h-4 w-4 mr-1" />
              {isSaved ? '保存済み' : '保存'}
            </Button>
            <Button onClick={handleCopy} size="sm" variant="outline">
              <Copy className="h-4 w-4 mr-1" />
              コピー
            </Button>
            <Button onClick={handleExportJson} size="sm" variant="outline">
              <FileCode className="h-4 w-4 mr-1" />
              JSON
            </Button>
            <Button onClick={handleExportMarkdown} size="sm" variant="outline">
              <FileText className="h-4 w-4 mr-1" />
              MD
            </Button>
            <Button onClick={handleExportHtml} size="sm" variant="outline">
              <Globe className="h-4 w-4 mr-1" />
              HTML
            </Button>
          </div>
        </div>
      </div>
      <div className="px-6 pb-6">
        <div className="wikipedia-style space-y-6">
          {/* 基本情報ボックス */}
          {Object.keys(result.infobox).length > 0 && (
            <div className="infobox border rounded-lg p-4 bg-gray-50">
              <h3 className="font-semibold text-lg mb-3">{result.title}</h3>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(result.infobox).map(([key, value]) => (
                    <tr key={key} className="border-t">
                      <td className="font-medium py-2 pr-4 text-gray-600">{key}</td>
                      <td className="py-2">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 導入部 */}
          {result.leadSection && (
            <div className="lead-section">
              <p className="text-base leading-relaxed">{result.leadSection}</p>
            </div>
          )}

          {/* 目次 */}
          {result.tableOfContents.length > 0 && (
            <div className="toc border rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold mb-2">目次</h3>
              <ol className="list-decimal list-inside space-y-1">
                {result.tableOfContents.map((item, index) => (
                  <li key={index} className="text-sm text-blue-700 hover:underline cursor-pointer">
                    {item}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* セクション */}
          {result.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="section">
              <h2 className="text-xl font-semibold mb-3 border-b pb-2">
                {section.title}
              </h2>
              <p className="mb-4 leading-relaxed">{section.content}</p>
              
              {section.subsections && section.subsections.map((subsection, subIndex) => (
                <div key={subIndex} className="ml-4 mb-4">
                  <h3 className="text-lg font-medium mb-2">{subsection.title}</h3>
                  <p className="leading-relaxed text-gray-700">{subsection.content}</p>
                </div>
              ))}
            </div>
          ))}

          {/* 関連項目 */}
          {result.seeAlso.length > 0 && (
            <div className="see-also">
              <h2 className="text-xl font-semibold mb-3 border-b pb-2">関連項目</h2>
              <ul className="list-disc list-inside space-y-1">
                {result.seeAlso.map((item, index) => (
                  <li key={index} className="text-blue-600 hover:underline cursor-pointer">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 参考文献 */}
          {result.references.length > 0 && (
            <div className="references">
              <h2 className="text-xl font-semibold mb-3 border-b pb-2">参考文献</h2>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                {result.references.map((ref, index) => (
                  <li key={index} className="text-gray-600">{ref}</li>
                ))}
              </ol>
            </div>
          )}

          {/* カテゴリ */}
          {result.categories.length > 0 && (
            <div className="categories border-t pt-4">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-500">カテゴリ:</span>
                {result.categories.map((cat, index) => (
                  <span key={index} className="text-sm text-blue-600 hover:underline cursor-pointer">
                    {cat}{index < result.categories.length - 1 && ' |'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function formatAsMarkdown(result: WikipediaResult): string {
  let markdown = `# ${result.title}\n\n`
  
  if (Object.keys(result.infobox).length > 0) {
    markdown += '## 基本情報\n\n'
    Object.entries(result.infobox).forEach(([key, value]) => {
      markdown += `- **${key}**: ${value}\n`
    })
    markdown += '\n'
  }
  
  if (result.leadSection) {
    markdown += `${result.leadSection}\n\n`
  }
  
  if (result.tableOfContents.length > 0) {
    markdown += '## 目次\n\n'
    result.tableOfContents.forEach((item, index) => {
      markdown += `${index + 1}. ${item}\n`
    })
    markdown += '\n'
  }
  
  result.sections.forEach((section) => {
    markdown += `## ${section.title}\n\n${section.content}\n\n`
    
    if (section.subsections) {
      section.subsections.forEach((subsection) => {
        markdown += `### ${subsection.title}\n\n${subsection.content}\n\n`
      })
    }
  })
  
  if (result.seeAlso.length > 0) {
    markdown += '## 関連項目\n\n'
    result.seeAlso.forEach((item) => {
      markdown += `- ${item}\n`
    })
    markdown += '\n'
  }
  
  if (result.references.length > 0) {
    markdown += '## 参考文献\n\n'
    result.references.forEach((ref, index) => {
      markdown += `${index + 1}. ${ref}\n`
    })
  }
  
  return markdown
}

function formatAsHtml(result: WikipediaResult): string {
  const infoboxHtml = Object.keys(result.infobox).length > 0 ? `
    <div style="float: right; width: 300px; margin: 0 0 20px 20px; padding: 15px; border: 1px solid #ddd; background: #f9f9f9;">
      <h3>${result.title}</h3>
      <table style="width: 100%;">
        ${Object.entries(result.infobox).map(([key, value]) => `
          <tr>
            <td style="font-weight: bold; padding: 5px;">${key}</td>
            <td style="padding: 5px;">${value}</td>
          </tr>
        `).join('')}
      </table>
    </div>
  ` : ''

  const sectionsHtml = result.sections.map(section => `
    <h2>${section.title}</h2>
    <p>${section.content}</p>
    ${section.subsections ? section.subsections.map(sub => `
      <h3>${sub.title}</h3>
      <p>${sub.content}</p>
    `).join('') : ''}
  `).join('')

  return `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <title>${result.title}</title>
  <style>
    body { font-family: serif; max-width: 1000px; margin: 0 auto; padding: 20px; }
    h1 { border-bottom: 3px solid #000; padding-bottom: 10px; }
    h2 { border-bottom: 1px solid #aaa; padding-bottom: 5px; margin-top: 30px; }
    h3 { margin-top: 20px; }
    .toc { background: #f9f9f9; border: 1px solid #ddd; padding: 15px; margin: 20px 0; }
  </style>
</head>
<body>
  <h1>${result.title}</h1>
  ${infoboxHtml}
  <p>${result.leadSection}</p>
  
  ${result.tableOfContents.length > 0 ? `
    <div class="toc">
      <h3>目次</h3>
      <ol>
        ${result.tableOfContents.map(item => `<li>${item}</li>`).join('')}
      </ol>
    </div>
  ` : ''}
  
  ${sectionsHtml}
  
  ${result.seeAlso.length > 0 ? `
    <h2>関連項目</h2>
    <ul>
      ${result.seeAlso.map(item => `<li>${item}</li>`).join('')}
    </ul>
  ` : ''}
  
  ${result.references.length > 0 ? `
    <h2>参考文献</h2>
    <ol>
      ${result.references.map(ref => `<li>${ref}</li>`).join('')}
    </ol>
  ` : ''}
  
  <hr style="margin-top: 50px;">
  <p style="text-align: center; color: #666;">
    カテゴリ: ${result.categories.join(' | ')}
  </p>
</body>
</html>`
}